import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tab,
    Tabs,
    Grid,
    Fab,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import LayersIcon from '@mui/icons-material/Layers';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import { API_BASE_URL } from '../config';
import EditLogs from './EditLogs';


// Define the layers we can manage
const MANAGED_LAYERS = [
    { id: 'mejhars', label: 'Mejhars' },
    { id: 'forages', label: 'Forages' }
];

interface LayerActionPanelProps {
    map: any; // mapboxgl.Map instance
    isOpen: boolean;
    onClose: () => void;
}

const LayerActionPanel: React.FC<LayerActionPanelProps> = ({ map, isOpen, onClose }) => {
    // const [isExpanded, setIsExpanded] = useState(false); // Removed internal state
    const [activeTab, setActiveTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [items, setItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editLogsOpen, setEditLogsOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState<any>(null); // null means new item
    const [formData, setFormData] = useState<any>({});
    const [coordinates, setCoordinates] = useState<{ lat: number, lng: number } | null>(null);
    const [lastEditTime, setLastEditTime] = useState(0);

    const currentLayerId = MANAGED_LAYERS[activeTab].id;
    const API_URL = `${API_BASE_URL}/api/layers`; // Should come from config

    // Fetch items when tab changes or panel opens
    useEffect(() => {
        if (isOpen) {
            fetchItems();
        }
    }, [currentLayerId, isOpen]);

    // Update Coordonnées field when coordinates change
    useEffect(() => {
        if (coordinates) {
            const { lat, lng } = coordinates;
            const latDir = lat >= 0 ? 'N' : 'S';
            const lngDir = lng >= 0 ? 'E' : 'W';
            const formatted = `${Math.abs(lat).toFixed(4)} ${latDir} ${Math.abs(lng).toFixed(4)} ${lngDir}`;

            setFormData((prev: any) => {
                const newData = { ...prev };
                // Update 'Coordonnées' if it exists or if we match the Forages format
                if ('Coordonnées' in newData || currentLayerId === 'forages') {
                    newData['Coordonnées'] = formatted;
                }
                // Update 'Coordonnées GPS' if it exists (Mejhars format)
                if ('Coordonnées GPS' in newData) {
                    // Keep established format for Mejhars if different, or adopt new standard?
                    // Mejhars used "lng lat" string in example, user asked for specific format.
                    // Let's standardise or update it. The user request was specific about the format.
                    // But strictly, user said "Coordonnées on edit...".
                    // For safety, I'll update Coordonnées GPS with the same format if it's there.
                    newData['Coordonnées GPS'] = formatted;
                }
                return newData;
            });
        }
    }, [coordinates, currentLayerId]);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/${currentLayerId}`, { credentials: 'include' });
            if (response.ok) {
                const data = await response.json();
                setItems(data.features || []);
            }
        } catch (error) {
            console.error('Error fetching items:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const filteredItems = items.filter(item => {
        const name = item.properties?.Nom || item.properties?.name || 'Unknown';
        return name.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const handleEdit = (item: any) => {
        setCurrentItem(item);
        const props = { ...item.properties };
        // Ensure Type is set based on layer if missing
        if (!props.Type) {
            props.Type = currentLayerId === 'mejhars' ? 'Mejhar' : 'Forage';
        }
        setFormData(props);

        if (item.geometry && item.geometry.type === 'Point') {
            setCoordinates({
                lng: item.geometry.coordinates[0],
                lat: item.geometry.coordinates[1]
            });
        }
        setEditDialogOpen(true);
    };

    const handleCreate = () => {
        setCurrentItem(null);
        // Initialize form with defaults
        // Initialize form with defaults based on layer type
        const commonFields = {
            'Nom': '',
            'Zone': '',
            'Wilaya': '',
            'Profondeur': '',
            'Débit': '',
            'Équipé': '',
            'Exploité par': '',
            'Date': '',
            'Type': currentLayerId === 'mejhars' ? 'Mejhar' : 'Forage'
        };

        const mejharFields = {
            'Statut': '',
            'Nombre d\'exploitants': '',
            'Total puits': '',
            'Puits actifs': '',
            'Puits non actifs': '',
            'Type d\'exploitation': '',
            'Date debut d\'exploitation': '',
            'Succursale': '',
            'Accidents enregistrés': '',
            'Date dernier recensement': '',
            'Coordonnées GPS': ''
        };

        const forageFields = {
            'Coordonnées': ''
        };

        if (currentLayerId === 'mejhars') {
            setFormData({ ...commonFields, ...mejharFields });
        } else {
            setFormData({ ...commonFields, ...forageFields });
        }

        // Default to map center if available, else 0,0
        if (map && map.getCenter) {
            const center = map.getCenter();
            setCoordinates({ lng: center.lng, lat: center.lat });
        } else {
            setCoordinates({ lng: 0, lat: 0 });
        }

        setEditDialogOpen(true);
    };

    const handleDelete = async (item: any) => {
        if (window.confirm('Are you sure you want to delete this item?')) {
            try {
                const id = item.id || item.properties?.id || item.properties?.OBJECTID;
                if (!id) {
                    alert('Error: Cannot delete item due to missing ID.');
                    return;
                }
                const response = await fetch(`${API_URL}/${currentLayerId}/${id}`, {
                    method: 'DELETE',
                    credentials: 'include'
                });

                if (response.ok) {
                    fetchItems(); // Refresh list
                    setLastEditTime(Date.now()); // Trigger logs refresh
                } else {
                    alert('Failed to delete item');
                }
            } catch (error) {
                console.error('Error deleting item:', error);
            }
        }
    };

    const handleSave = async () => {
        try {
            // Prepare payload
            let geometry;

            if (coordinates) {
                geometry = { type: 'Point', coordinates: [Number(coordinates.lng), Number(coordinates.lat)] };
            } else if (currentItem) {
                geometry = currentItem.geometry;
            } else {
                alert('Geometry is required');
                return;
            }

            const method = currentItem ? 'PUT' : 'POST';

            // Extract ID safely
            let id = '';
            if (currentItem) {
                id = currentItem.id || currentItem.properties?.id || currentItem.properties?.OBJECTID;
                if (!id) {
                    console.error('Item has no ID:', currentItem);
                    alert('Error: Cannot save item due to missing ID.');
                    return;
                }
            }

            const url = currentItem ? `${API_URL}/${currentLayerId}/${id}` : `${API_URL}/${currentLayerId}`;

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ properties: formData, geometry }),
                credentials: 'include'
            });

            if (response.ok) {
                setEditDialogOpen(false);
                fetchItems();
                setLastEditTime(Date.now()); // Trigger logs refresh
            } else {
                alert('Failed to save item');
            }
        } catch (error) {
            console.error('Error saving item:', error);
        }
    };

    const flyToItem = (item: any) => {
        if (!map) return;
        if (item.geometry.type === 'Point') {
            map.flyTo({ center: item.geometry.coordinates, zoom: 14 });
        }
    };

    // Helper to render dynamic form fields
    const renderFormFields = () => {
        // Collect all potential keys from formData
        // For new items, we might want to have some default fields based on the layer type
        const defaultKeys = ['Nom', 'Wilaya', 'Zone', 'Type']; // Common fields
        const allKeys = Array.from(new Set([...defaultKeys, ...Object.keys(formData)]));

        // Filter out internal/system keys if necessary
        const displayKeys = allKeys.filter(k => !['OBJECTID', 'id', 'deleted', 'created_at', 'updated_at', 'created_by', 'deleted_at', 'deleted_by'].includes(k));

        return (
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Location (Point)</Typography>
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label="Latitude"
                        type="number"
                        fullWidth
                        value={coordinates?.lat || 0}
                        onChange={(e) => setCoordinates(prev => ({ ...prev!, lat: parseFloat(e.target.value) }))}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label="Longitude"
                        type="number"
                        fullWidth
                        value={coordinates?.lng || 0}
                        onChange={(e) => setCoordinates(prev => ({ ...prev!, lng: parseFloat(e.target.value) }))}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Properties</Typography>
                </Grid>

                {displayKeys.map(key => (
                    <Grid item xs={12} sm={6} key={key}>
                        {key === 'Type' ? (
                            <TextField
                                label="Type"
                                fullWidth
                                size="small"
                                value={formData[key] || (currentLayerId === 'mejhars' ? 'Mejhar' : 'Forage')}
                                disabled
                            />
                        ) : (
                            <TextField
                                label={key}
                                fullWidth
                                size="small"
                                value={formData[key] || ''}
                                onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                                disabled={key === 'Coordonnées' || key === 'Coordonnées GPS'}
                            />
                        )}
                    </Grid>
                ))}
            </Grid>
        );
    };

    if (!isOpen) {
        return null;
    }

    return (
        <>
            <EditLogs isOpen={editLogsOpen} onClose={() => setEditLogsOpen(false)} refreshTrigger={lastEditTime} />

            <Paper sx={{
                position: 'absolute',
                top: 80, // Moved down to avoid header coverage if needed, or keep at top
                right: 20, // Moved to right side
                width: 350,
                maxHeight: 'calc(100vh - 100px)',
                display: 'flex',
                flexDirection: 'column',
                zIndex: 1000,
                p: 1,
                overflow: 'hidden' // prevent scrolling on main container
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" sx={{ ml: 1 }}>Layer Manager</Typography>
                    <Box>
                        <Tooltip title="View Edit Logs">
                            <IconButton size="small" onClick={() => setEditLogsOpen(!editLogsOpen)} color={editLogsOpen ? 'primary' : 'default'}>
                                <HistoryIcon />
                            </IconButton>
                        </Tooltip>
                        <IconButton size="small" onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>

                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    variant="fullWidth"
                    textColor="primary"
                    indicatorColor="primary"
                >
                    {MANAGED_LAYERS.map((layer, index) => (
                        <Tab key={layer.id} label={layer.label} />
                    ))}
                </Tabs>

                <Box sx={{ p: 1, display: 'flex', alignItems: 'center' }}>
                    <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                    <TextField
                        fullWidth
                        placeholder="Search..."
                        variant="standard"
                        value={searchTerm}
                        onChange={handleSearch}
                    />
                    <IconButton onClick={handleCreate} color="primary">
                        <AddIcon />
                    </IconButton>
                </Box>

                <List sx={{ overflowY: 'auto', flexGrow: 1 }}>
                    {filteredItems.map((item, index) => (
                        <ListItem key={index} button onClick={() => flyToItem(item)}>
                            <ListItemText
                                primary={item.properties?.Nom || item.properties?.name || 'Unknown'}
                                secondary={item.properties?.Wilaya || item.properties?.Zone}
                            />
                            <ListItemSecondaryAction>
                                <IconButton edge="end" onClick={() => handleEdit(item)}>
                                    <EditIcon />
                                </IconButton>
                                <IconButton edge="end" onClick={() => handleDelete(item)}>
                                    <DeleteIcon />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>

                <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>{currentItem ? 'Edit Item' : 'Create Item'}</DialogTitle>
                    <DialogContent>
                        {renderFormFields()}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleSave} color="primary">Save</Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </>
    );
};

export default LayerActionPanel;
